// src/components/SponsorSpot.js
//
// 掲載中のスポンサーから1件をランダムに選んで表示する部品。
// アプリ内のどこにでも1行で差し込める。
//
// 【使い方】
//   import SponsorSpot from './components/SponsorSpot';
//
//   <SponsorSpot variant="text" setActiveTab={setActiveTab} />     ← テキストのみ
//   <SponsorSpot variant="banner" setActiveTab={setActiveTab} />   ← バナー付き
//
//   setActiveTab は省略可。渡すと、リンク先URLが無いスポンサーを
//   タップしたときに特典一覧へ遷移する。
//
// 【コストについて】
//   settings/sponsors は1ドキュメントだが、置いた場所の数だけ
//   読み取りが発生すると無駄なので、モジュールレベルでキャッシュする。
//   同じセッション中は何箇所に置いても読み取りは1回だけ。
//   既定の保持時間は30分（users_public のキャッシュを60分にしたのと同じ考え方）。
//
// 【表示しない条件】
//   - スポンサー機能が無効（enabled === false）
//   - 掲載中のスポンサーが0件
//   - 掲載期間外 / 停止中
//   いずれの場合も null を返すので、レイアウトに隙間ができない。
import React, { useState, useEffect } from 'react';
import { db } from '../firebase';
import { doc, getDoc } from 'firebase/firestore';

// ---- モジュールレベルのキャッシュ ----------------------------------
let _cache = { data: null, at: 0 };
const CACHE_MS = 30 * 60 * 1000;   // 30分

// 管理画面で内容を変えた直後に反映したいときに呼ぶ
export const clearSponsorSpotCache = () => { _cache = { data: null, at: 0 }; };

const fetchSponsors = async () => {
    const now = Date.now();
    if (_cache.data && (now - _cache.at) < CACHE_MS) return _cache.data;
    const snap = await getDoc(doc(db, 'settings', 'sponsors'));
    const data = snap.exists() ? snap.data() : { items: [], enabled: true };
    _cache = { data, at: now };
    return data;
};

const todayIso = () => {
    const d = new Date();
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};

const isOnAir = (item) => {
    if (!item) return false;
    if (item.active === false) return false;
    const t = todayIso();
    if (item.validFrom && item.validFrom > t) return false;
    if (item.validUntil && item.validUntil < t) return false;
    return true;
};

const SponsorSpot = ({ variant = 'text', setActiveTab = null, style = {} }) => {
    const [item, setItem] = useState(null);

    useEffect(() => {
        let alive = true;
        (async () => {
            try {
                const data = await fetchSponsors();
                if (!alive) return;
                if (data.enabled === false) return;
                const list = (Array.isArray(data.items) ? data.items : []).filter(isOnAir);
                if (list.length === 0) return;
                // 表示のたびに1件をランダムに選ぶ（掲載社数が少ないうちは均等に回る）
                setItem(list[Math.floor(Math.random() * list.length)]);
            } catch (e) {
                console.error('スポンサー取得エラー:', e);
            }
        })();
        return () => { alive = false; };
    }, []);

    if (!item) return null;   // 掲載が無ければ何も描画しない

    const handleClick = () => {
        if (item.url) {
            window.open(item.url, '_blank', 'noopener,noreferrer');
        } else if (setActiveTab) {
            setActiveTab('sponsors');
        }
    };

    // ---- テキストのみ ----------------------------------------------
    if (variant === 'text') {
        return (
            <div
                onClick={handleClick}
                style={{
                    display: 'flex', alignItems: 'center', gap: '8px',
                    padding: '10px 12px', background: '#fafafa',
                    border: '1px solid #eee', borderRadius: '8px',
                    cursor: 'pointer', ...style
                }}
            >
                <span style={{
                    fontSize: '9px', background: '#eee', color: '#888',
                    padding: '2px 6px', borderRadius: '4px', fontWeight: 'bold', flexShrink: 0
                }}>PR</span>
                <div style={{ flex: 1, minWidth: 0 }}>
                    <span style={{ fontSize: '12px', fontWeight: 'bold', color: '#444' }}>{item.name}</span>
                    {item.catch && (
                        <span style={{
                            fontSize: '11px', color: '#888', marginLeft: '6px',
                            whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis'
                        }}>
                            {item.catch}
                        </span>
                    )}
                </div>
                {item.couponCode && (
                    <span style={{
                        fontSize: '10px', background: '#fff3e0', color: '#e65100',
                        padding: '3px 8px', borderRadius: '10px', fontWeight: 'bold', flexShrink: 0
                    }}>特典あり</span>
                )}
            </div>
        );
    }

    // ---- バナー付き --------------------------------------------------
    return (
        <div
            onClick={handleClick}
            style={{
                display: 'flex', alignItems: 'center', gap: '12px',
                padding: '12px', background: 'white',
                border: '1px solid #eee', borderRadius: '10px',
                cursor: 'pointer', boxShadow: '0 1px 3px rgba(0,0,0,0.06)', ...style
            }}
        >
            {item.logoUrl ? (
                <img
                    src={item.logoUrl}
                    alt={item.name}
                    style={{
                        width: '56px', height: '56px', objectFit: 'contain',
                        borderRadius: '8px', border: '1px solid #eee', background: 'white', flexShrink: 0
                    }}
                />
            ) : (
                <div style={{
                    width: '56px', height: '56px', background: '#f5f5f5', borderRadius: '8px',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    fontSize: '22px', flexShrink: 0
                }}>🏪</div>
            )}
            <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '3px' }}>
                    <span style={{
                        fontSize: '9px', background: '#eee', color: '#888',
                        padding: '2px 6px', borderRadius: '4px', fontWeight: 'bold'
                    }}>PR</span>
                    {item.couponCode && (
                        <span style={{
                            fontSize: '10px', background: '#fff3e0', color: '#e65100',
                            padding: '2px 8px', borderRadius: '10px', fontWeight: 'bold'
                        }}>会員特典あり</span>
                    )}
                </div>
                <div style={{
                    fontWeight: 'bold', fontSize: '14px', color: '#333',
                    whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis'
                }}>
                    {item.name}
                </div>
                {item.catch && (
                    <div style={{
                        fontSize: '11px', color: '#888', lineHeight: '1.5',
                        whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis'
                    }}>
                        {item.catch}
                    </div>
                )}
            </div>
            <span style={{ fontSize: '16px', color: '#ccc', flexShrink: 0 }}>›</span>
        </div>
    );
};

export default SponsorSpot;